/*
 * Boston Proper line-item properties (LIPs) appender.
 *
 * Defines the <bp-line-item-props> custom element GLOBALLY (loaded from
 * layout/theme.liquid) so it upgrades wherever the element appears —
 * including when the quick-view drawer morphs a product's PDP content into
 * itself. The block (blocks/bp-line-item-props.liquid) only renders the
 * element + a JSON data island; the behavior lives here.
 *
 * Why global (not a block {% javascript %}): a block's script only loads on
 * pages that render the block (the PDP). The quick-view drawer can be opened
 * from a PLP / homepage / "How to wear it" where the block isn't present, so
 * the definition must exist page-wide for the morphed element to upgrade and
 * attach the LIPs. QA 7/16: LIPs were missing when adding from the quick-view
 * drawer (the old inline <script> IIFE never re-ran on morph).
 */
(function () {
  'use strict';

  // Global helper for JS-based add-to-cart paths that don't go through the
  // product form (e.g. the Stylitics "shop the look" drawer). Fetches a
  // product's LIP data from the ?view=lips endpoint and builds the properties
  // object to merge into /cart/add.js — so those adds carry the same LIPs as
  // the PDP / quick-view form.
  if (!window.bpLips) {
    const cache = {};
    window.bpLips = {
      fetch(handle) {
        if (!handle) return Promise.resolve(null);
        if (cache[handle]) return cache[handle];
        const p = window
          .fetch('/products/' + encodeURIComponent(handle) + '?view=lips', { credentials: 'same-origin' })
          .then((r) => (r.ok ? r.json() : null))
          .catch(() => null);
        cache[handle] = p;
        return p;
      },
      // Build the { properties } object for a given variant id from LIP data.
      // Mirrors the block: _final_sale always set; the rest only when non-empty.
      propsForVariant(data, variantId) {
        const props = {};
        if (!data) return props;
        const s = data.static || {};
        Object.keys(s).forEach((k) => {
          if (s[k] !== '' && s[k] != null) props[k] = String(s[k]);
        });
        const v = (data.variants || {})[String(variantId)];
        if (v) {
          if (v._catalog_id) props._catalog_id = String(v._catalog_id);
          if (v._price_status) props._price_status = String(v._price_status);
          const cap = Number(v.compare_at_price || 0);
          if (cap > 0) props._compare_at_price = cap.toFixed(2);
          if (v.price) props._price = String(v.price);
        }
        return props;
      },
      // Convenience: fetch + build in one call.
      forVariant(handle, variantId) {
        return this.fetch(handle).then((data) => this.propsForVariant(data, variantId));
      },

      // ---- Outfit-bundle upsell helpers (Atlas parity) ----
      // Atlas's useAddToCart tags outfit-bundle upsell adds with
      // _upsell_type/_upsell_area plus a new-line-vs-quantity-bump flag:
      // _is_bundle_product when the add creates a brand-new cart line,
      // _is_upsell when it only bumps an existing line's quantity
      // (useAddToCart.js:228-273). _upsell_type is always "outfit-bundle";
      // _upsell_area is "cart-line-items" per the BP metafield sheet.

      // Current cart line for a variant, or null if it isn't in the bag.
      // One /cart.js GET — the caller is a user-gesture add, so the extra
      // round-trip is fine. Used to decide add.js (new line) vs
      // change.js (quantity bump).
      cartLineForVariant(variantId) {
        const vid = Number(variantId);
        return window
          .fetch('/cart.js', { credentials: 'same-origin', headers: { Accept: 'application/json' } })
          .then((r) => (r.ok ? r.json() : null))
          .then((cart) => {
            if (!cart || !Array.isArray(cart.items)) return null;
            return cart.items.find((it) => Number(it.variant_id) === vid) || null;
          })
          .catch(() => null);
      },

      // Merge the outfit-bundle upsell flags onto a base props object.
      // isNewLine === false → quantity bump (_is_upsell); otherwise new
      // line (_is_bundle_product). Booleans match Atlas's payload.
      withUpsellProps(baseProps, opts) {
        const o = opts || {};
        const props = Object.assign({}, baseProps || {});
        props._upsell_type = 'outfit-bundle';
        props._upsell_area = o.area || 'cart-line-items';
        if (o.isNewLine === false) props._is_upsell = true;
        else props._is_bundle_product = true;
        return props;
      },

      // One-call outfit-bundle add. Branches like Atlas's useAddToCart:
      //   variant already in the bag → /cart/change.js, bump qty, _is_upsell
      //   otherwise                  → /cart/add.js, new line,   _is_bundle_product
      // Base LIPs come from the shared ?view=lips data so an upsell line
      // carries the same metafield properties as a PDP / quick-view add.
      // Returns the fetch Response.
      addUpsell(a) {
        const args = a || {};
        const variantId = args.variantId;
        const quantity = args.quantity || 1;
        const area = args.area || 'cart-line-items';
        if (!variantId) return Promise.reject(new Error('addUpsell: variantId required'));
        return Promise.all([
          this.forVariant(args.handle, variantId).catch(() => ({})),
          this.cartLineForVariant(variantId)
        ]).then((results) => {
          const baseProps = results[0] || {};
          const existingLine = results[1];
          if (existingLine && existingLine.key) {
            const merged = this.withUpsellProps(
              Object.assign({}, existingLine.properties || {}, baseProps),
              { area: area, isNewLine: false }
            );
            return window.fetch('/cart/change.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
              credentials: 'same-origin',
              body: JSON.stringify({
                id: existingLine.key,
                quantity: (existingLine.quantity || 0) + quantity,
                properties: merged
              })
            });
          }
          const props = this.withUpsellProps(baseProps, { area: area, isNewLine: true });
          return window.fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            credentials: 'same-origin',
            body: JSON.stringify({ items: [{ id: variantId, quantity: quantity, properties: props }] })
          });
        });
      }
    };
  }

  if (customElements.get('bp-line-item-props')) return;

  class BpLineItemProps extends HTMLElement {
    connectedCallback() {
      const dataEl = this.querySelector('script.bp-lip-data');
      if (!dataEl) return;
      let data;
      try { data = JSON.parse(dataEl.textContent || '{}'); } catch (_) { return; }
      this.staticProps = data.static || {};
      this.variants = data.variants || {};
      this.initialVid = parseInt(this.dataset.initialVid, 10);

      this.onVariant = (e) => {
        const vid = e && e.detail && e.detail.resource && e.detail.resource.id;
        if (vid) this.sync(vid);
      };
      document.addEventListener('variant:update', this.onVariant);

      // Delay one frame so the ATC form has parsed / been morphed in.
      requestAnimationFrame(() => this.sync(this.initialVid));
    }

    disconnectedCallback() {
      if (this.onVariant) document.removeEventListener('variant:update', this.onVariant);
    }

    // Locate the ATC form for THIS instance's surface. In the quick-view
    // drawer the LIP block + the (redistributed) product form both live inside
    // the drawer <dialog>, so scoping to the nearest dialog attaches the LIPs
    // to the DRAWER form, not the underlying PDP form. On the PDP there's no
    // dialog, so it falls through to the section.
    findForm() {
      const scope =
        this.closest('dialog') ||
        this.closest('#quick-add-modal-content') ||
        this.closest('[data-section-id]') ||
        this.closest('.shopify-section') ||
        document;
      const f = scope.querySelector('form[action$="/cart/add"]');
      if (f && f.querySelector('input[name="id"]')) return f;
      return document.querySelector('form[action$="/cart/add"]');
    }

    setLip(form, key, value) {
      const fullName = 'properties[' + key + ']';
      let input = form.querySelector('input[name="' + fullName + '"][data-bp-lip]');
      if (!input) {
        input = document.createElement('input');
        input.type = 'hidden';
        input.name = fullName;
        input.setAttribute('data-bp-lip', key);
        form.appendChild(input);
      }
      // Empty / falsy → disable so the input is omitted from FormData.
      if (value === '' || value == null) {
        input.value = '';
        input.disabled = true;
      } else {
        input.value = String(value);
        input.disabled = false;
      }
    }

    sync(vid) {
      const form = this.findForm();
      if (!form) return;
      Object.keys(this.staticProps).forEach((k) => this.setLip(form, k, this.staticProps[k]));
      const v = this.variants[String(vid)];
      if (v) {
        this.setLip(form, '_catalog_id', v._catalog_id || '');
        this.setLip(form, '_price_status', v._price_status || '');
        const cap = Number(v.compare_at_price || 0);
        this.setLip(form, '_compare_at_price', cap > 0 ? cap.toFixed(2) : '');
        this.setLip(form, '_price', v.price || '');
      }
    }
  }

  customElements.define('bp-line-item-props', BpLineItemProps);
})();
